import React from "react";
import { motion } from "framer-motion";
import SectionHead from "../SectionHead";
import { Section, Kicker, Paragraph } from "./common";
import { container, item, inView } from "./presets";
import { Check, ShieldCheck } from "lucide-react";

const T = (dir, ar, en) => (dir === "rtl" ? ar : en);

function splitChecklist(group) {
  const intro = group.nodes.filter((n) => n.t === "p");
  const rest = group.nodes.filter((n) => n.t === "h4" || n.t === "ul");
  const allItems = group.nodes.filter((n) => n.t === "ul").flatMap((n) => n.v);
  return { intro, rest, allItems };
}

/* ---------- Variant: Split (heading + list card) ---------- */
function ChecklistSplit({ group, dark, dir }) {
  const { intro, rest } = splitChecklist(group);
  return (
    <Section dark={dark}>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
        <motion.div {...inView} variants={container}>
          <motion.div variants={item}>
            <Kicker>{T(dir, "لماذا روبوكويس", "Why RoboQuess")}</Kicker>
          </motion.div>
          <motion.h2 variants={item} className="font-display mt-4 text-2xl md:text-[34px] font-bold text-white leading-tight">{group.title}</motion.h2>
          {intro.map((n, i) => (
            <motion.div key={i} variants={item}>
              <Paragraph>{n.v}</Paragraph>
            </motion.div>
          ))}
        </motion.div>
        <motion.div {...inView} variants={container} className="rq-card p-7 md:p-9">
          {rest.map((n, i) =>
            n.t === "h4" ? (
              <motion.h3 key={i} variants={item} className="font-display text-lg font-semibold text-white mb-5 mt-6 first:mt-0">{n.v}</motion.h3>
            ) : (
              <div key={i} className="grid gap-3">
                {n.v.map((li, k) => (
                  <motion.div key={k} variants={item} whileHover={{ x: dir === "rtl" ? -4 : 4 }} className="flex items-start gap-3 rounded-xl border border-[color:var(--rq-border)] bg-[rgba(14,122,58,0.04)] px-4 py-3">
                    <span className="grid place-items-center h-6 w-6 rounded-full bg-[rgba(14,122,58,0.15)] text-[color:var(--rq-green)] shrink-0 mt-0.5"><Check size={13} /></span>
                    <span className="text-sm leading-relaxed text-white">{li}</span>
                  </motion.div>
                ))}
              </div>
            )
          )}
        </motion.div>
      </div>
    </Section>
  );
}

/* ---------- Variant: Feature grid ---------- */
function ChecklistGrid({ group, dark, dir }) {
  const { intro, allItems } = splitChecklist(group);
  return (
    <Section dark={dark}>
      <SectionHead tag={T(dir, "لماذا روبوكويس", "Why RoboQuess")} title={group.title} desc={intro[0] ? intro[0].v : undefined} center />
      <motion.div {...inView} variants={container} className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {allItems.map((li, i) => (
          <motion.div key={i} variants={item} whileHover={{ y: -6 }} className="rq-card p-6 h-full flex items-start gap-4 group">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-[rgba(14,122,58,0.12)] text-[color:var(--rq-green)] shrink-0 transition-transform duration-300 group-hover:scale-110"><ShieldCheck size={20} /></span>
            <span className="text-sm md:text-[15px] leading-relaxed text-white">{li}</span>
          </motion.div>
        ))}
      </motion.div>
    </Section>
  );
}

export function Checklist({ variant, ...props }) {
  if (variant === "grid") return <ChecklistGrid {...props} />;
  return <ChecklistSplit {...props} />;
}
